/**
 * Antakshari - Song Battle Game
 * Modes: Solo vs AI, Multiplayer (AI judged), Weekly Tournament
 */

import React, { Fragment, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Helmet } from "react-helmet";
import { AuthContext } from '../context/authContext';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import Avatar from '@mui/material/Avatar';
import alertService from '../services/sweetAlert';
import './Antakshari.css';

const API_URL = process.env.REACT_APP_API_END_POINT;
const SOCKET_URL = process.env.REACT_APP_SOCKET_END_POINT || API_URL;

export default function Antakshari() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Game mode state
  const [gameMode, setGameMode] = useState(null); // null, 'solo', 'multiplayer', 'tournament'
  const [showModeSelect, setShowModeSelect] = useState(true);

  // Solo mode state
  const [soloGame, setSoloGame] = useState(null);
  const [soloRounds, setSoloRounds] = useState([]);
  const [isMyTurnSolo, setIsMyTurnSolo] = useState(false);
  const [currentLetterSolo, setCurrentLetterSolo] = useState('');
  const [aiThinking, setAiThinking] = useState(false);
  const [aiCurrentSong, setAiCurrentSong] = useState(null); // Currently singing song
  const [soloTimeRemaining, setSoloTimeRemaining] = useState(60);

  // Speech recognition state
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [speechSupported, setSpeechSupported] = useState(false);

  // Tournament state
  const [tournament, setTournament] = useState(null);
  const [planets, setPlanets] = useState([]);
  const [isNominated, setIsNominated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Match state
  const [match, setMatch] = useState(null);
  const [matchState, setMatchState] = useState(null);
  const [planet1Players, setPlanet1Players] = useState([]);
  const [planet2Players, setPlanet2Players] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(45);

  // Input state
  const [songLine, setSongLine] = useState('');
  const [songTitle, setSongTitle] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');

  // View state
  const [view, setView] = useState('nominations'); // nominations, match, leaderboard
  const [leaderboard, setLeaderboard] = useState([]);
  const [dailyTop10, setDailyTop10] = useState([]);

  // Voice mode state
  const [gamePhase, setGamePhase] = useState('waiting'); // waiting, playing, singing, voting
  const [currentSinger, setCurrentSinger] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [votes, setVotes] = useState({ valid: 0, invalid: 0 });
  const [showVoiceRoom, setShowVoiceRoom] = useState(false);

  // All planets from groups table
  const [allPlanets, setAllPlanets] = useState([]);

  // Audio playback for AI singing (uses Google Cloud TTS from server)
  const audioRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Play audio from base64 data
  const playAIAudio = useCallback((audioData) => {
    if (!audioData || !audioData.audioContent) return;

    try {
      // Create blob from base64
      const byteCharacters = atob(audioData.audioContent);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: audioData.contentType || 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(blob);

      // Create or reuse audio element
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.onended = () => {
          setIsAudioPlaying(false);
          URL.revokeObjectURL(audioRef.current.src);
        };
        audioRef.current.onerror = () => {
          setIsAudioPlaying(false);
          console.log('Audio playback error');
        };
      }

      audioRef.current.src = audioUrl;
      audioRef.current.volume = 1.0;
      setIsAudioPlaying(true);

      audioRef.current.play().catch(err => {
        console.log('Audio autoplay blocked:', err.message);
        setIsAudioPlaying(false);
      });
    } catch (err) {
      console.log('Error playing AI audio:', err);
    }
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setSpeechSupported(true);
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'hi-IN'; // Hindi

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }

        setTranscript(finalTranscript || interimTranscript);
        if (finalTranscript) {
          setSongLine(prev => (prev ? prev + ' ' : '') + finalTranscript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Toggle speech recognition
  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setTranscript('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);

  // Reset transcript when turn changes (no auto-start - user clicks button)
  useEffect(() => {
    if (isMyTurnSolo) {
      setTranscript('');
      setSongLine('');
    }
  }, [isMyTurnSolo]);

  // Fetch tournament data
  useEffect(() => {
    fetchTournamentData();
    fetchAllPlanets();
  }, [currentUser]);

  const fetchAllPlanets = async () => {
    try {
      const res = await fetch(`${API_URL}/antakshari/planets`);
      const data = await res.json();
      setAllPlanets(data.planets || []);
    } catch (error) {
      console.error('Error fetching planets:', error);
    }
  };

  const fetchTournamentData = async () => {
    try {
      setLoading(true);

      // Get current tournament
      const tournamentRes = await fetch(`${API_URL}/antakshari/tournament`);
      const tournamentData = await tournamentRes.json();

      if (tournamentData.active) {
        setTournament(tournamentData.tournament);

        // Get nominations/planets
        const nomRes = await fetch(`${API_URL}/antakshari/nominations`);
        const nomData = await nomRes.json();
        setPlanets(nomData.planets || []);

        // Check if current user is nominated
        if (currentUser) {
          const checkRes = await fetch(`${API_URL}/antakshari/check-nomination/${currentUser.SID}`);
          const checkData = await checkRes.json();
          setIsNominated(checkData.nominated);
        }

        // Get today's match
        const matchRes = await fetch(`${API_URL}/antakshari/match/today`);
        const matchData = await matchRes.json();
        if (matchData.match) {
          setMatch(matchData.match);
          setPlanet1Players(matchData.planet1Players || []);
          setPlanet2Players(matchData.planet2Players || []);

          if (matchData.match.status === 'live') {
            setView('match');
            connectToMatch(matchData.match.id);
          }
        }

        // Get leaderboard
        const lbRes = await fetch(`${API_URL}/antakshari/leaderboard`);
        const lbData = await lbRes.json();
        setLeaderboard(lbData.leaderboard || []);

        // Get daily top 10
        const top10Res = await fetch(`${API_URL}/antakshari/daily-top10`);
        const top10Data = await top10Res.json();
        setDailyTop10(top10Data.top10 || []);
      }
    } catch (error) {
      console.error('Error fetching tournament data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Connect to match socket
  const connectToMatch = (matchId) => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    socketRef.current = io(`${SOCKET_URL}/antakshari`, {
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('Connected to Antakshari');
      socketRef.current.emit('joinMatch', {
        matchId,
        playerId: currentUser?.SID,
        playerName: currentUser?.name,
        planetName: currentUser?.gname
      });
    });

    socketRef.current.on('matchState', (state) => {
      setMatchState(state);
      setRounds(state.rounds || []);
      setTimeRemaining(Math.ceil(state.turnTimeRemaining / 1000));
    });

    socketRef.current.on('matchStarted', (data) => {
      alertService.info('Match Started!', `${data.firstTurn === 'planet1' ? match?.planet1_name : match?.planet2_name} goes first with letter: ${data.letter}`);
    });

    socketRef.current.on('turnStarted', (data) => {
      setMatchState(prev => ({
        ...prev,
        currentPlanetTurn: data.planet,
        currentLetter: data.letter,
        currentRound: data.round,
        planet1Score: data.planet1Score,
        planet2Score: data.planet2Score
      }));
      setTimeRemaining(Math.ceil(data.timeLimit / 1000));
      setGamePhase('playing');
      setCurrentSinger(null);
      setMyVote(null);
      setVotes({ valid: 0, invalid: 0 });
    });

    // Voice mode events
    socketRef.current.on('singingStarted', (data) => {
      setCurrentSinger(data.singer);
      setGamePhase('singing');
      setTimeRemaining(Math.ceil(data.timeLimit / 1000));
    });

    socketRef.current.on('votingStarted', (data) => {
      setGamePhase('voting');
      setTimeRemaining(Math.ceil(data.timeLimit / 1000));
      setMyVote(null);
    });

    socketRef.current.on('voteReceived', (data) => {
      setVotes({ valid: data.totalValid, invalid: data.totalInvalid });
    });

    socketRef.current.on('roundComplete', (data) => {
      setRounds(prev => [...prev, data]);
      setMatchState(prev => ({
        ...prev,
        planet1Score: data.planet1Score,
        planet2Score: data.planet2Score,
        currentLetter: data.nextLetter
      }));
      setGamePhase('playing');

      // Show AI judgement result
      if (data.aiJudgement) {
        if (data.aiJudgement.valid) {
          alertService.success('AI Approved! ✓', `${data.singer?.playerName} earned ${data.points} points!`);
        } else {
          alertService.error('AI Rejected ✗', data.aiJudgement.reason || 'Song does not match the required letter');
        }
      } else if (data.isValid) {
        alertService.success('Valid!', `${data.singer?.playerName} earned ${data.points} points!`);
      } else {
        alertService.info('Invalid', `Song not accepted.`);
      }
    });

    socketRef.current.on('songSubmitted', (data) => {
      setRounds(prev => [...prev, data]);
      setMatchState(prev => ({
        ...prev,
        planet1Score: data.planet1Score,
        planet2Score: data.planet2Score
      }));

      if (data.isValid) {
        // Show success notification
      }
    });

    socketRef.current.on('turnTimeout', (data) => {
      setMatchState(prev => ({
        ...prev,
        planet1Score: data.planet1Score,
        planet2Score: data.planet2Score
      }));
      setGamePhase('playing');
    });

    socketRef.current.on('matchEnded', (data) => {
      alertService.success('Match Ended!', `Winner: ${data.winnerName} (${data.planet1Score} - ${data.planet2Score})`);
      setMatchState(prev => ({ ...prev, status: 'ended' }));
      fetchTournamentData();
    });

    socketRef.current.on('chatMessage', (data) => {
      setChatMessages(prev => [...prev.slice(-50), data]);
    });

    socketRef.current.on('error', (data) => {
      alertService.error('Error', data.message);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  };

  // Connect to solo game socket
  const connectToSoloGame = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    socketRef.current = io(`${SOCKET_URL}/antakshari`, {
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('Connected to Antakshari Solo Mode');
      socketRef.current.emit('startSoloGame', {
        playerId: currentUser?.SID,
        playerName: currentUser?.name,
        difficulty: 'medium'
      });
    });

    // Solo game started
    socketRef.current.on('soloGameStarted', (data) => {
      setSoloGame({
        gameId: data.gameId,
        playerScore: 0,
        aiScore: 0,
        round: 1,
        maxRounds: data.maxRounds
      });
      setCurrentLetterSolo(data.firstLetter);
      setIsMyTurnSolo(data.isPlayerFirst);
      setSoloRounds([]);
      setSoloTimeRemaining(60);
      setShowModeSelect(false);

      if (data.isPlayerFirst) {
        alertService.info('Your Turn!', `Sing a song starting with: ${data.firstLetter}`);
      } else {
        setAiThinking(true);
        alertService.info('AI Goes First', 'AI is thinking of a song...');
      }
    });

    // AI is thinking
    socketRef.current.on('soloAIThinking', (data) => {
      setIsMyTurnSolo(false);
      setAiThinking(true);
    });

    // Player's round result
    socketRef.current.on('soloRoundResult', (data) => {
      setSoloRounds(prev => [...prev, {
        type: 'player',
        songLine: data.songLine,
        letter: data.letter,
        isValid: data.isValid,
        points: data.points,
        reason: data.reason
      }]);

      setSoloGame(prev => ({
        ...prev,
        playerScore: data.playerScore,
        aiScore: data.aiScore,
        round: data.round
      }));

      if (data.isValid) {
        alertService.success('Valid!', `+${data.points} points!`);
      } else {
        alertService.error('Invalid', data.reason || 'Song does not start with the correct letter. -2 penalty, AI\'s turn!');
      }
      // Always go to AI's turn after player submits
      setAiThinking(true);
      setIsMyTurnSolo(false);

      setSongLine('');
      setTranscript('');
    });

    // AI sang a song
    socketRef.current.on('soloAISang', (data) => {
      setAiThinking(false);

      // Show AI's song visually
      setAiCurrentSong({
        title: data.song.title,
        lyrics: data.song.lyrics
      });

      // Play AI's singing audio if available (from Google Cloud TTS)
      if (data.audio) {
        playAIAudio(data.audio);
      }

      // Clear visual display after 8 seconds (longer to allow audio to play)
      setTimeout(() => setAiCurrentSong(null), 8000);

      setSoloRounds(prev => [...prev, {
        type: 'ai',
        songTitle: data.song.title,
        songLyrics: data.song.lyrics,
        letter: data.letter,
        points: 10
      }]);

      setSoloGame(prev => ({
        ...prev,
        aiScore: data.aiScore,
        round: data.round
      }));

      setCurrentLetterSolo(data.nextLetter);
      setIsMyTurnSolo(true);
      setSoloTimeRemaining(60);

      alertService.info('Your Turn!', `AI sang "${data.song.title}". Now sing a song starting with: ${data.nextLetter}`);
    });

    // AI couldn't find a song
    socketRef.current.on('soloAISkipped', (data) => {
      setAiThinking(false);
      setSoloRounds(prev => [...prev, {
        type: 'ai',
        skipped: true,
        letter: data.letter,
        points: 0
      }]);

      setCurrentLetterSolo(data.nextLetter);
      setIsMyTurnSolo(true);
      setSoloTimeRemaining(60);

      alertService.info('AI Skipped!', `AI couldn't find a song. Your turn with: ${data.nextLetter}`);
    });

    // Player's turn notification
    socketRef.current.on('soloYourTurn', (data) => {
      setCurrentLetterSolo(data.letter);
      setIsMyTurnSolo(true);
      setSoloTimeRemaining(data.timeLimit / 1000);
    });

    // Game ended
    socketRef.current.on('soloGameEnded', (data) => {
      setSoloGame(prev => ({
        ...prev,
        playerScore: data.playerScore,
        aiScore: data.aiScore,
        ended: true
      }));

      if (data.winner === 'player') {
        alertService.success('You Won!', `Final: ${data.playerScore} - ${data.aiScore}`);
      } else if (data.winner === 'ai') {
        alertService.info('AI Won', `Final: ${data.playerScore} - ${data.aiScore}`);
      } else {
        alertService.info('Draw!', `Final: ${data.playerScore} - ${data.aiScore}`);
      }
    });

    socketRef.current.on('error', (data) => {
      alertService.error('Error', data.message);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  };

  // Solo mode: Submit song
  const handleSoloSubmitSong = () => {
    if (!songLine.trim()) {
      alertService.error('Error', 'Please enter or sing a song line');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    }

    socketRef.current?.emit('soloSubmitSong', {
      songLine: songLine.trim(),
      songTitle: songTitle.trim()
    });

    setIsMyTurnSolo(false);
  };

  // Solo mode: Skip turn
  const handleSoloSkip = () => {
    socketRef.current?.emit('soloSkipTurn');
    setIsMyTurnSolo(false);
    setAiThinking(true);
    setSoloRounds(prev => [...prev, {
      type: 'player',
      skipped: true,
      letter: currentLetterSolo,
      points: -5
    }]);
    setSoloGame(prev => ({
      ...prev,
      playerScore: Math.max(0, prev.playerScore - 5)
    }));
  };

  // Solo mode: Play again
  const handlePlayAgain = () => {
    setSoloGame(null);
    setSoloRounds([]);
    connectToSoloGame();
  };

  // Solo mode: Back to menu
  const handleBackToMenu = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    setSoloGame(null);
    setSoloRounds([]);
    setShowModeSelect(true);
    setGameMode(null);
  };

  // Timer countdown for solo mode
  useEffect(() => {
    if (soloGame && isMyTurnSolo && soloTimeRemaining > 0 && !soloGame.ended) {
      const timer = setInterval(() => {
        setSoloTimeRemaining(prev => {
          if (prev <= 1) {
            handleSoloSkip();
            return 60;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [soloGame, isMyTurnSolo, soloGame?.ended]);

  // Timer countdown
  useEffect(() => {
    if (matchState?.status === 'playing' && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [matchState?.status, matchState?.currentRound]);

  // Nominate self
  const handleNominate = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (!currentUser.gname || currentUser.gname === 'General') {
      alertService.error('No Planet', 'You must be part of a planet to participate! Join a planet first.');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/antakshari/nominate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerId: currentUser.SID,
          playerName: currentUser.name,
          planetName: currentUser.gname
        })
      });

      const data = await res.json();
      if (data.success) {
        alertService.success('Nominated!', 'You have been nominated for Antakshari!');
        setIsNominated(true);
        fetchTournamentData();
      } else {
        alertService.error('Error', data.error);
      }
    } catch (error) {
      alertService.error('Error', 'Failed to nominate');
    }
  };

  // Submit song
  const handleSubmitSong = () => {
    if (!songLine.trim()) {
      alertService.error('Error', 'Please enter a song line');
      return;
    }

    socketRef.current?.emit('submitSong', {
      matchId: match?.id,
      songLine: songLine.trim(),
      songTitle: songTitle.trim()
    });

    setSongLine('');
    setSongTitle('');
  };

  // Skip turn
  const handleSkip = () => {
    socketRef.current?.emit('skipTurn', { matchId: match?.id });
  };

  // Voice mode: Start singing
  const handleStartSinging = () => {
    socketRef.current?.emit('startSinging', { matchId: match?.id });
  };

  // Voice mode: End singing
  const handleEndSinging = () => {
    socketRef.current?.emit('endSinging', { matchId: match?.id, songTitle: songTitle.trim() });
    setSongTitle('');
  };

  // Voice mode: Vote on song
  const handleVote = (vote) => {
    socketRef.current?.emit('voteOnSong', { matchId: match?.id, vote });
    setMyVote(vote);
  };

  // Open voice room in new tab
  const openVoiceRoom = () => {
    window.open('/voice', '_blank');
    setShowVoiceRoom(true);
  };

  // Send chat message
  const handleSendChat = () => {
    if (!chatInput.trim()) return;

    socketRef.current?.emit('chatMessage', {
      matchId: match?.id,
      message: chatInput.trim()
    });

    setChatInput('');
  };

  // Check if it's user's team's turn
  const isMyTurn = () => {
    if (!matchState || !currentUser) return false;
    const myPlanet = currentUser.gname === match?.planet1_name ? 'planet1' :
                     currentUser.gname === match?.planet2_name ? 'planet2' : null;
    return myPlanet && matchState.currentPlanetTurn === myPlanet;
  };

  // Render loading
  if (loading && !soloGame) {
    return (
      <div className="main-content antakshari-page">
      <Helmet><title>Antakshari - Play the Classic Hindi Song Game | Geet Suhane</title></Helmet>
        <div className="antakshari-loading">
          <div className="loading-spinner"></div>
          <p>Loading Antakshari...</p>
        </div>
      </div>
    );
  }

  // Render Solo Mode
  if (gameMode === 'solo' && soloGame) {
    return (
      <div className="main-content antakshari-page">
        <div className="antakshari-container">
          {/* Solo Header */}
          <div className="antakshari-header">
            <div className="header-icon">🤖</div>
            <div className="header-text">
              <h1>Solo vs AI</h1>
              <p>Round {soloGame.round} of {soloGame.maxRounds || 10}</p>
            </div>
            <button className="back-btn" onClick={handleBackToMenu}>✕</button>
          </div>

          {/* Turn Indicator Banner */}
          {!soloGame.ended && (
            <div className={`turn-banner ${isMyTurnSolo ? 'your-turn' : 'ai-turn'}`}>
              {isMyTurnSolo ? (
                <>
                  <span className="turn-icon">🎤</span>
                  <span className="turn-text">YOUR TURN</span>
                  <span className="turn-timer">{soloTimeRemaining}s</span>
                </>
              ) : (
                <>
                  <span className="turn-icon">🤖</span>
                  <span className="turn-text">AI's TURN</span>
                  <span className="turn-timer">{aiThinking ? '🎵' : '--'}</span>
                </>
              )}
            </div>
          )}

          {/* Solo Scoreboard */}
          <div className="solo-scoreboard">
            <div className={`solo-player ${isMyTurnSolo ? 'active' : ''}`}>
              <div className="player-label">You</div>
              <div className="player-score">{soloGame.playerScore}</div>
            </div>
            <div className="solo-vs">
              <div className="current-letter-solo">
                {currentLetterSolo}
              </div>
              <div className="letter-label">Letter</div>
            </div>
            <div className={`solo-player ${!isMyTurnSolo && !soloGame.ended ? 'active' : ''}`}>
              <div className="player-label">AI</div>
              <div className="player-score">{soloGame.aiScore}</div>
            </div>
          </div>

          {/* Game Ended */}
          {soloGame.ended ? (
            <div className="solo-game-ended">
              <div className="result-icon">
                {soloGame.playerScore > soloGame.aiScore ? '🏆' :
                 soloGame.playerScore < soloGame.aiScore ? '😔' : '🤝'}
              </div>
              <h2>
                {soloGame.playerScore > soloGame.aiScore ? 'You Won!' :
                 soloGame.playerScore < soloGame.aiScore ? 'AI Won!' : "It's a Draw!"}
              </h2>
              <p className="final-score">
                Final: {soloGame.playerScore} - {soloGame.aiScore}
              </p>
              <div className="game-ended-buttons">
                <button className="play-again-btn" onClick={handlePlayAgain}>
                  🔄 Play Again
                </button>
                <button className="back-menu-btn" onClick={handleBackToMenu}>
                  ← Back to Menu
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* AI Thinking */}
              {aiThinking && (
                <div className="ai-thinking">
                  <div className="thinking-animation">🤔</div>
                  <p>AI is thinking of a song...</p>
                </div>
              )}

              {/* AI Singing Display */}
              {aiCurrentSong && !aiThinking && (
                <div className={`ai-singing ${isAudioPlaying ? 'audio-playing' : ''}`}>
                  <div className="singing-icon">🎤🤖</div>
                  <div className="song-display">
                    <div className="ai-song-title">🎵 {aiCurrentSong.title}</div>
                    <div className="ai-song-lyrics">{aiCurrentSong.lyrics}</div>
                  </div>
                  <div className="speaking-indicator">
                    {isAudioPlaying ? '🔊 AI is singing...' : '🎵 AI sang this song'}
                  </div>
                </div>
              )}

              {/* My Turn Input */}
              {isMyTurnSolo && !aiThinking && (
                <div className="solo-input-area">
                  <div className="input-prompt">
                    🎵 Sing a song starting with: <strong className="letter-highlight">{currentLetterSolo}</strong>
                  </div>

                  {/* Speech Recognition Button */}
                  {speechSupported && (
                    <>
                      <button
                        className={`mic-btn ${isListening ? 'listening' : ''}`}
                        onClick={toggleListening}
                      >
                        {isListening ? '🔴 सुन रहा हूँ... (Listening)' : '🎤 गाना गाएं (Tap to Sing)'}
                      </button>
                      <div className="language-hint">
                        🗣️ Hindi/हिंदी mode - Sing in Hindi for best results
                      </div>
                    </>
                  )}

                  {/* Transcript display */}
                  {transcript && (
                    <div className="transcript-preview">
                      <span className="transcript-label">सुना:</span> {transcript}
                    </div>
                  )}

                  {/* Text Input */}
                  <textarea
                    ref={inputRef}
                    className="song-input"
                    placeholder="Type or sing a song line..."
                    value={songLine}
                    onChange={(e) => setSongLine(e.target.value)}
                    rows={2}
                  />

                  <input
                    type="text"
                    className="song-title-input"
                    placeholder="Song title (optional)"
                    value={songTitle}
                    onChange={(e) => setSongTitle(e.target.value)}
                  />

                  <div className="solo-action-buttons">
                    <button
                      className="submit-song-btn"
                      onClick={handleSoloSubmitSong}
                      disabled={!songLine.trim()}
                    >
                      ✓ Submit Song
                    </button>
                    <button className="skip-btn" onClick={handleSoloSkip}>
                      ⏭️ Skip (-5 pts)
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Rounds History */}
          <div className="solo-rounds-history">
            <h3>Song History</h3>
            {soloRounds.slice().reverse().map((round, idx) => (
              <div key={idx} className={`solo-round-item ${round.type} ${round.isValid === false ? 'invalid' : ''} ${round.skipped ? 'skipped' : ''}`}>
                <div className="round-icon">
                  {round.type === 'player' ? '👤' : '🤖'}
                </div>
                <div className="round-content">
                  <div className="round-letter">{round.letter}</div>
                  {round.skipped ? (
                    <div className="skipped-text">Skipped turn</div>
                  ) : round.type === 'ai' ? (
                    <>
                      <div className="song-title">🎵 {round.songTitle}</div>
                      <div className="song-lyrics">{round.songLyrics}</div>
                    </>
                  ) : (
                    <div className="song-line">{round.songLine}</div>
                  )}
                  {round.reason && <div className="round-reason">{round.reason}</div>}
                </div>
                <div className={`round-points ${round.points > 0 ? 'positive' : round.points < 0 ? 'negative' : ''}`}>
                  {round.points > 0 ? '+' : ''}{round.points}
                </div>
              </div>
            ))}
            {soloRounds.length === 0 && (
              <p className="no-rounds">No songs yet. Start singing!</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render Mode Selection
  if (showModeSelect && !soloGame) {
    return (
      <div className="main-content antakshari-page">
        <div className="antakshari-container">
          {/* Header */}
          <div className="antakshari-header">
            <div className="header-icon">🎤</div>
            <div className="header-text">
              <h1>Antakshari</h1>
              <p>The Classic Song Battle Game</p>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="mode-selection">
            <h2>Choose Your Mode</h2>

            {/* Solo vs AI */}
            <div
              className="mode-card solo"
              onClick={() => {
                if (!currentUser) {
                  navigate('/login');
                  return;
                }
                setGameMode('solo');
                connectToSoloGame();
              }}
            >
              <div className="mode-icon">🤖</div>
              <div className="mode-info">
                <h3>Solo vs AI</h3>
                <p>Practice with our AI opponent. AI judges your songs and sings back!</p>
              </div>
              <div className="mode-arrow">→</div>
            </div>

            {/* Multiplayer */}
            <div
              className="mode-card multiplayer"
              onClick={() => {
                setGameMode('multiplayer');
                setShowModeSelect(false);
              }}
            >
              <div className="mode-icon">👥</div>
              <div className="mode-info">
                <h3>Multiplayer</h3>
                <p>Play with others. AI validates songs - no peer voting needed!</p>
              </div>
              <div className="mode-arrow">→</div>
            </div>

            {/* Tournament */}
            <div
              className="mode-card tournament"
              onClick={() => {
                setGameMode('tournament');
                setShowModeSelect(false);
              }}
            >
              <div className="mode-icon">🏆</div>
              <div className="mode-info">
                <h3>Weekly Tournament</h3>
                <p>Planet vs Planet battle every Sunday! Win GSC rewards.</p>
                <span className="mode-tag">Sundays Only</span>
              </div>
              <div className="mode-arrow">→</div>
            </div>
          </div>

          {/* Quick Tips */}
          <div className="quick-tips">
            <h3>📋 How to Play</h3>
            <ul>
              <li>🎵 Sing or type a song starting with the given letter</li>
              <li>🔤 The last letter of your song becomes the next letter</li>
              <li>🤖 AI validates songs using Hindi/English letter matching</li>
              <li>⚡ Faster responses = bonus points!</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="main-content antakshari-page">
        <div className="antakshari-container">
          {/* Header */}
          <div className="antakshari-header">
            <div className="header-icon">{gameMode === 'tournament' ? '🏆' : '👥'}</div>
            <div className="header-text">
              <h1>{gameMode === 'tournament' ? 'Tournament' : 'Multiplayer'}</h1>
              <p>{tournament?.name || 'Antakshari Battle'}</p>
            </div>
            <button className="back-btn" onClick={handleBackToMenu}>✕</button>
          </div>

          {/* Navigation Tabs */}
          <div className="antakshari-tabs">
            <button
              className={`tab ${view === 'nominations' ? 'active' : ''}`}
              onClick={() => setView('nominations')}
            >
              Nominations
            </button>
            <button
              className={`tab ${view === 'match' ? 'active' : ''}`}
              onClick={() => setView('match')}
              disabled={!match}
            >
              Live Match
            </button>
            <button
              className={`tab ${view === 'leaderboard' ? 'active' : ''}`}
              onClick={() => setView('leaderboard')}
            >
              Leaderboard
            </button>
          </div>

          {/* Nominations View */}
          {view === 'nominations' && (
            <div className="nominations-view">
              {/* Nominate Button */}
              {!isNominated && currentUser && (
                <div className="nominate-section">
                  <button className="nominate-btn" onClick={handleNominate}>
                    🎵 Nominate Yourself
                  </button>
                  <p>Join your planet's team for Antakshari!</p>
                </div>
              )}

              {isNominated && (
                <div className="nominated-badge">
                  ✅ You are nominated!
                </div>
              )}

              {/* Today's Match Info */}
              {match && (
                <div className="today-match">
                  <h3>Today's Match {match.status === 'live' ? '🔴 LIVE NOW' : 'at 6 PM'}</h3>
                  <div className="match-preview">
                    <div className="team team1">
                      <div className="team-name">{match.planet1_name}</div>
                      <div className="team-count">{planet1Players.length} players</div>
                    </div>
                    <div className="vs">VS</div>
                    <div className="team team2">
                      <div className="team-name">{match.planet2_name}</div>
                      <div className="team-count">{planet2Players.length} players</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {match.status === 'live' ? (
                      <button className="join-match-btn" onClick={() => {
                        setView('match');
                        connectToMatch(match.id);
                      }}>
                        🔴 Join Live Match
                      </button>
                    ) : match.status === 'scheduled' && (
                      <>
                        <button className="join-match-btn" style={{ background: '#4CAF50' }} onClick={async () => {
                          try {
                            const res = await fetch(`${process.env.REACT_APP_API_END_POINT}/antakshari/start-match`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ matchId: match.id })
                            });
                            const data = await res.json();
                            if (data.success) {
                              setMatch(data.match);
                              setView('match');
                              connectToMatch(match.id);
                            } else {
                              alert(data.error || 'Failed to start match');
                            }
                          } catch (err) {
                            console.error('Error starting match:', err);
                          }
                        }}>
                          ▶️ Start Match
                        </button>
                        <button className="join-match-btn" onClick={() => {
                          setView('match');
                          connectToMatch(match.id);
                        }}>
                          👀 Join Waiting Room
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Planets List - Show nominations if available, otherwise all planets */}
              <div className="planets-list">
                <h3>Planet Rankings {planets.length > 0 ? '(by nominations)' : ''}</h3>
                {planets.length > 0 ? (
                  planets.map((planet, idx) => (
                    <div key={planet.planet_id} className={`planet-item ${planet.has_played ? 'played' : ''}`} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: planet.players?.length > 0 ? '10px' : 0 }}>
                        <span className="rank">#{idx + 1}</span>
                        <span className="name" style={{ flex: 1 }}>{planet.planet_name}</span>
                        <span className="nominations">{planet.nomination_count} nominated</span>
                        {planet.has_played && <span className="played-badge">Played</span>}
                      </div>
                      {/* Show nominated players */}
                      {planet.players?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingLeft: '35px' }}>
                          {planet.players.map((player, pIdx) => (
                            <div key={player.player_id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: 'rgba(255,255,255,0.1)',
                              padding: '4px 10px 4px 4px',
                              borderRadius: '20px',
                              fontSize: '12px'
                            }}>
                              <Avatar
                                src={player.picture ? (
                                  player.picture.includes('googleusercontent') || player.picture.startsWith('http')
                                    ? player.picture
                                    : `https://files.geetsuhane.com/users/${player.picture}`
                                ) : 'https://files.geetsuhane.com/users/default.png'}
                                sx={{ width: 24, height: 24 }}
                              />
                              <span style={{ color: 'rgba(255,255,255,0.9)' }}>
                                {player.player_name?.split(' ')[0]}
                              </span>
                              {player.daily_points > 0 && (
                                <span style={{ color: '#ffd700', fontSize: '10px' }}>
                                  ({player.daily_points})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  allPlanets.map((planet, idx) => (
                    <div key={planet.planet_id} className="planet-item">
                      <span className="rank">#{idx + 1}</span>
                      <span className="name">{planet.planet_name}</span>
                      <span className="nominations">{planet.member_count} members</span>
                    </div>
                  ))
                )}
                {planets.length === 0 && allPlanets.length === 0 && (
                  <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '20px' }}>
                    No nominations yet. Be the first to nominate!
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Match View */}
          {view === 'match' && match && (
            <div className="match-view">
              {/* Voice Room Button */}
              <div className="voice-room-banner">
                <button className="voice-room-btn" onClick={openVoiceRoom}>
                  🎙️ {showVoiceRoom ? 'Voice Room Open' : 'Open Voice Room'}
                </button>
                <span className="voice-hint">Join the voice room to sing live!</span>
              </div>

              {/* Scoreboard */}
              <div className="scoreboard">
                <div className={`team-score ${matchState?.currentPlanetTurn === 'planet1' ? 'active' : ''}`}>
                  <div className="team-name">{match.planet1_name}</div>
                  <div className="score">{matchState?.planet1Score || 0}</div>
                </div>
                <div className="match-info">
                  <div className="round">Round {matchState?.currentRound || 0}</div>
                  <div className={`timer ${timeRemaining <= 10 ? 'warning' : ''}`}>
                    {timeRemaining}s
                  </div>
                  <div className="current-letter">
                    Letter: <span>{matchState?.currentLetter || '?'}</span>
                  </div>
                  {/* Game Phase Indicator */}
                  <div className={`phase-indicator ${gamePhase}`}>
                    {gamePhase === 'playing' && '🎯 Ready to Sing'}
                    {gamePhase === 'singing' && '🎤 Singing...'}
                    {gamePhase === 'voting' && '🗳️ Vote Now!'}
                    {gamePhase === 'waiting' && '⏳ Waiting...'}
                  </div>
                </div>
                <div className={`team-score ${matchState?.currentPlanetTurn === 'planet2' ? 'active' : ''}`}>
                  <div className="team-name">{match.planet2_name}</div>
                  <div className="score">{matchState?.planet2Score || 0}</div>
                </div>
              </div>

              {/* Voice Mode Controls - My Turn to Sing */}
              {matchState?.status === 'playing' && isMyTurn() && gamePhase === 'playing' && (
                <div className="voice-input-area">
                  <div className="input-header">
                    🎵 Your Turn! Sing a song starting with: <strong>{matchState.currentLetter}</strong>
                  </div>
                  <button className="start-singing-btn" onClick={handleStartSinging}>
                    🎤 Start Singing
                  </button>
                  <p className="voice-hint">Click to claim your turn, then sing in the voice room!</p>
                </div>
              )}

              {/* Currently Singing - Show Done Button */}
              {gamePhase === 'singing' && currentSinger && (
                <div className="singing-area">
                  <div className="singer-info">
                    <div className="singer-avatar">🎤</div>
                    <div className="singer-name">{currentSinger.playerName} is singing...</div>
                  </div>
                  {currentSinger.playerId === currentUser?.SID && (
                    <div className="done-singing-section">
                      <input
                        type="text"
                        placeholder="Song title (optional)"
                        value={songTitle}
                        onChange={(e) => setSongTitle(e.target.value)}
                        className="song-title-input"
                      />
                      <button className="done-singing-btn" onClick={handleEndSinging}>
                        ✅ Done Singing
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* AI Judging Phase */}
              {gamePhase === 'voting' && (
                <div className="ai-judging-area">
                  <div className="ai-judging-header">
                    🤖 AI is validating the song...
                  </div>
                  <div className="ai-thinking-animation">
                    <span className="dot">.</span>
                    <span className="dot">.</span>
                    <span className="dot">.</span>
                  </div>
                  <p className="ai-judging-hint">
                    Checking if song starts with <strong>{matchState?.currentLetter}</strong>
                  </p>
                </div>
              )}

              {/* Waiting for other team */}
              {matchState?.status === 'playing' && !isMyTurn() && gamePhase === 'playing' && (
                <div className="waiting-turn">
                  Waiting for {matchState.currentPlanetTurn === 'planet1' ? match.planet1_name : match.planet2_name} to sing...
                </div>
              )}

              {/* Rounds History */}
              <div className="rounds-history">
                <h3>Song History</h3>
                {rounds.slice().reverse().map((round, idx) => (
                  <div key={idx} className={`round-item ${round.isValid ? 'valid' : 'invalid'}`}>
                    <div className="round-header">
                      <Avatar src={round.picture} sx={{ width: 24, height: 24 }} />
                      <span className="player-name">{round.playerName}</span>
                      <span className="ai-badge">{round.isValid ? '🤖✓' : '🤖✗'}</span>
                      <span className={`points ${round.points > 0 ? 'positive' : 'negative'}`}>
                        {round.points > 0 ? '+' : ''}{round.points}
                      </span>
                    </div>
                    <div className="song-line">
                      <span className="letter">{round.letter}</span>
                      {round.songLine}
                    </div>
                    {round.songTitle && <div className="song-title">🎵 {round.songTitle}</div>}
                    {round.aiJudgement?.reason && !round.isValid && (
                      <div className="ai-reason">AI: {round.aiJudgement.reason}</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Chat */}
              <div className="match-chat">
                <div className="chat-messages">
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className="chat-msg">
                      <strong>{msg.playerName}:</strong> {msg.message}
                    </div>
                  ))}
                </div>
                <div className="chat-input">
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
                  />
                  <button onClick={handleSendChat}>Send</button>
                </div>
              </div>
            </div>
          )}

          {/* Leaderboard View */}
          {view === 'leaderboard' && (
            <div className="leaderboard-view">
              {/* Daily Top 10 */}
              <div className="daily-top10">
                <h3>🏆 Today's Top 10 (10 GSC each)</h3>
                {dailyTop10.length > 0 ? (
                  dailyTop10.map((player, idx) => (
                    <div key={player.player_id} className={`top10-item rank-${idx + 1}`}>
                      <span className="rank">{idx + 1}</span>
                      <span className="name">{player.player_name}</span>
                      <span className="points">{player.points_earned} pts</span>
                      <span className="correct">{player.correct_answers}/{player.rounds_played}</span>
                    </div>
                  ))
                ) : (
                  <p className="no-data">No matches played today</p>
                )}
              </div>

              {/* Tournament Standings */}
              <div className="tournament-standings">
                <h3>Tournament Standings</h3>
                {leaderboard.map((planet, idx) => (
                  <div key={planet.planet_id} className={`standing-item ${planet.qualified_for_playoffs ? 'qualified' : ''}`}>
                    <span className="rank">#{idx + 1}</span>
                    <span className="name">{planet.planet_name}</span>
                    <span className="stats">
                      W:{planet.matches_won} L:{planet.matches_lost}
                    </span>
                    <span className="score">{planet.total_score} pts</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
}
